import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, UserCog, Shield, MapPin, BellRing, Lock, Unlock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "boe_admin_token";
const REGIONS = ["Bern", "Basel", "Lausanne"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });
const emptyForm = () => ({ name: "", password: "", rank: "Mitglied", region: "Bern", leadership: false, announce_role: "none" });

const fieldClass = "h-11 rounded-xl bg-white/5 border-white/15 text-cream placeholder:text-cream/40 focus-visible:ring-gold/40 focus-visible:border-gold/60";
const selectClass = "h-11 w-full rounded-xl border border-white/15 bg-anthracite text-cream px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold/60";

const formatError = (detail, fb) => (typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((e) => e?.msg).join(" ") : fb);

const UsersPanel = ({ onLogout }) => {
  const [users, setUsers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeader() });
      setUsers(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Benutzer konnten nicht geladen werden.");
      setUsers([]);
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, password: "", rank: u.rank, region: u.region, leadership: !!u.leadership, announce_role: u.announce_role || "none" }); setDialogOpen(true); };
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name.trim(), rank: form.rank.trim(), region: form.region, leadership: form.leadership, announce_role: form.announce_role };
        if (form.password.trim()) payload.password = form.password;
        const { data } = await axios.put(`${API}/admin/users/${editing.id}`, payload, { headers: authHeader() });
        setUsers((prev) => prev.map((u) => (u.id === editing.id ? data : u)));
        toast.success("Benutzer aktualisiert.");
      } else {
        if (!form.password.trim()) { toast.error("Bitte ein Passwort vergeben."); setSaving(false); return; }
        const { data } = await axios.post(`${API}/admin/users`, {
          name: form.name.trim(), password: form.password, rank: form.rank.trim() || "Mitglied", region: form.region, leadership: form.leadership, announce_role: form.announce_role,
        }, { headers: authHeader() });
        setUsers((prev) => [...prev, data]);
        toast.success("Benutzer erstellt.");
      }
      setDialogOpen(false);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatError(err.response?.data?.detail, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers: authHeader() });
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("Benutzer gelöscht.");
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  const [actionId, setActionId] = useState(null);

  const warnUser = async (u) => {
    setActionId(u.id);
    try {
      const { data } = await axios.post(`${API}/admin/users/${u.id}/warn`, {}, { headers: authHeader() });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(`Warn-Banner an ${u.name} gesendet.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Warn-Banner konnte nicht gesendet werden.");
    } finally {
      setActionId(null);
    }
  };

  const toggleLock = async (u) => {
    setActionId(u.id);
    try {
      const { data } = await axios.put(`${API}/admin/users/${u.id}/lock`, { locked: !u.locked }, { headers: authHeader() });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success(!u.locked ? `${u.name} gesperrt.` : `${u.name} entsperrt.`);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error("Sperre konnte nicht geändert werden.");
    } finally {
      setActionId(null);
    }
  };

  if (users === null) return <div className="flex items-center justify-center py-24 text-cream/60"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div data-testid="users-panel">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="section-eyebrow">Zugänge</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-cream tracking-tight">Benutzer</h1>
          <p className="mt-2 text-sm text-cream/60">Konten für das Mitglieder-Portal verwalten.</p>
        </div>
        <button onClick={openAdd} data-testid="users-add" className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-soft text-anthracite px-5 py-2.5 text-sm font-semibold transition-colors">
          <Plus size={16} /> Benutzer erstellen
        </button>
      </div>

      {users.length === 0 ? (
        <p className="text-sm text-cream/45 py-12 text-center border border-dashed border-white/10 rounded-2xl" data-testid="users-empty">
          Noch keine Benutzer. Erstelle den ersten Zugang oben rechts.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gold/15" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-cream/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-5 py-3">Name</th>
                <th className="text-left font-semibold px-5 py-3">Rang</th>
                <th className="text-left font-semibold px-5 py-3">Region</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-white/8 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 font-semibold text-cream">
                    {u.name}
                    {u.leadership && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gold/15 border border-gold/30 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#E5C76B" }}>Leitung</span>
                    )}
                    {u.announce_role === "vertreter" && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gold/10 border border-gold/25 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#E5C76B" }}>Vertreter</span>
                    )}
                    {u.announce_role === "admin" && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-gold/20 border border-gold/40 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color: "#E5C76B" }}>Ankündig.-Admin</span>
                    )}
                    {u.locked && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-500/15 border border-red-500/40 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-red-300" data-testid={`user-locked-badge-${u.id}`}><Lock size={10} /> Gesperrt</span>
                    )}
                    {u.security_banner && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-400/15 border border-amber-400/40 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold text-amber-300" data-testid={`user-warn-badge-${u.id}`}><BellRing size={10} /> Warnung offen</span>
                    )}
                  </td>
                  <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5 text-xs" style={{ color: "#E5C76B" }}><Shield size={13} /> {u.rank}</span></td>
                  <td className="px-5 py-3 text-cream/75"><span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-cream/50" /> {u.region}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button onClick={() => warnUser(u)} disabled={actionId === u.id} data-testid={`user-warn-${u.id}`} aria-label="Warn-Banner senden" title="Warn-Banner senden (Nutzer muss neuen Login bestätigen)" className="inline-flex items-center gap-1 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2.5 py-1.5 text-[11px] font-semibold text-amber-300 hover:bg-amber-400 hover:text-anthracite transition-colors disabled:opacity-50">
                        {actionId === u.id ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />} <span className="hidden sm:inline">Warnen</span>
                      </button>
                      <button onClick={() => toggleLock(u)} disabled={actionId === u.id} data-testid={`user-lock-${u.id}`} aria-label={u.locked ? "Entsperren" : "Sperren"} title={u.locked ? "Konto entsperren" : "Konto sofort sperren"} className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 ${u.locked ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400 hover:text-anthracite" : "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-600 hover:text-white"}`}>
                        {actionId === u.id ? <Loader2 size={14} className="animate-spin" /> : u.locked ? <Unlock size={14} /> : <Lock size={14} />} <span className="hidden sm:inline">{u.locked ? "Entsperren" : "Sperren"}</span>
                      </button>
                      <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} aria-label="Bearbeiten" className="text-cream/60 hover:text-gold-soft transition-colors"><Pencil size={15} /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`user-delete-${u.id}`} disabled={deletingId === u.id} aria-label="Löschen" className="text-cream/50 hover:text-red-400 transition-colors disabled:opacity-50">
                            {deletingId === u.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-anthracite-soft border border-gold/20 text-cream">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-cream">Benutzer löschen?</AlertDialogTitle>
                            <AlertDialogDescription className="text-cream/60">
                              Konto von <span className="text-cream/90 font-medium">{u.name}</span> wirklich löschen? Der Zugang wird sofort entzogen.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-white/20 text-cream hover:bg-white/10 hover:text-cream">Abbrechen</AlertDialogCancel>
                            <AlertDialogAction data-testid={`user-delete-confirm-${u.id}`} onClick={() => remove(u.id)} className="bg-red-600 text-white hover:bg-red-700">Löschen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-anthracite-soft border border-gold/20 text-cream sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cream flex items-center gap-2">
              <UserCog size={18} style={{ color: "#E5C76B" }} />
              {editing ? "Benutzer bearbeiten" : "Benutzer erstellen"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2" data-testid="user-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Name (Login)</span>
              <Input value={form.name} onChange={update("name")} required data-testid="user-form-name" className={fieldClass} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">{editing ? "Neues Passwort (leer = unverändert)" : "Passwort"}</span>
              <Input type="text" value={form.password} onChange={update("password")} data-testid="user-form-password" className={fieldClass} />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Rang</span>
                <Input value={form.rank} onChange={update("rank")} data-testid="user-form-rank" className={fieldClass} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Region</span>
                <select value={form.region} onChange={update("region")} data-testid="user-form-region" className={selectClass}>
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  <option value="Alle">Alle Regionen</option>
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Zusatzrechte</span>
              <select
                value={form.leadership ? "yes" : "no"}
                onChange={(e) => setForm((f) => ({ ...f, leadership: e.target.value === "yes" }))}
                data-testid="user-form-leadership"
                className={selectClass}
              >
                <option value="no">Keine (nur Kreis-Chat)</option>
                <option value="yes">Leitung (zusätzlich Leiter-Chat)</option>
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-cream/55">Ankündigungs-Rolle</span>
              <select
                value={form.announce_role}
                onChange={(e) => setForm((f) => ({ ...f, announce_role: e.target.value }))}
                data-testid="user-form-announce-role"
                className={selectClass}
              >
                <option value="none">Keine (nur lesen)</option>
                <option value="vertreter">Vertreter (eigener Kreis schreiben)</option>
                <option value="admin">Admin (alle Kreise schreiben)</option>
              </select>
            </label>
            <DialogFooter className="mt-2">
              <button type="submit" disabled={saving} data-testid="user-form-submit" className="btn-primary w-full disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Speichern…</> : editing ? "Änderungen speichern" : "Erstellen"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPanel;
